import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Divider, ButtonBase, Drawer, TextField, MenuItem, Switch, Snackbar, Alert, IconButton, AppBar, Toolbar } from '@mui/material';
import React, { useState, useEffect } from 'react';
import {
  Person2Outlined,
  EmailOutlined,
  LanguageOutlined,
  RestartAltOutlined,
  StarBorderOutlined,
  FeedbackOutlined,
  PersonAddAltOutlined,
  ChevronRight,
  LogoutOutlined,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const SettingsItem = ({ icon, primary, secondary, onClick, showSwitch, switchState, onSwitchChange }) => (
  <ButtonBase
    onClick={onClick}
    component="div"
    sx={{
      width: '100%',
      textAlign: 'left',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
      transition: 'all 0.3s ease',
      borderRadius: '12px',
      mb: 2,
    }}
  >
    <ListItem sx={{ py: 2, width: '100%' }}>
      <ListItemIcon>
        <Box
          sx={{
            backgroundColor: 'primary.lighter',
            borderRadius: '12px',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px',
          }}
        >
          {React.cloneElement(icon, { sx: { color: 'primary.main' } })}
        </Box>
      </ListItemIcon>
      <ListItemText 
        primary={<Typography sx={{ color: 'primary.main', fontWeight: 500 }}>{primary}</Typography>}
        secondary={<Typography sx={{ color: 'text.secondary' }}>{secondary}</Typography>}
      />
      {showSwitch ? (
        <Switch
          checked={switchState}
          onChange={(e) => {
            e.stopPropagation();
            onSwitchChange(e.target.checked);
          }}
          color="primary"
        />
      ) : (
        <ChevronRight sx={{ color: 'text.secondary', mr: 1 }} />
      )}
    </ListItem>
  </ButtonBase>
);

const SettingsDrawer = ({ open, onClose, title, children }) => (
  <Drawer
    anchor="bottom"
    open={open}
    onClose={onClose}
    sx={{
      '& .MuiDrawer-paper': {
        background: 'transparent',
        boxShadow: 'none'
      }
    }}
  >
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'center',
        p: 2
      }}
      onClick={onClose}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: '90%',
          maxWidth: '600px',
          bgcolor: 'background.paper',
          borderRadius: '25px',
          position: 'fixed',
          pb: 4,
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 48,
            height: 4,
            backgroundColor: 'grey.300',
            borderRadius: 2
          }
        }}
      >
        <AppBar 
          position="sticky" 
          color="transparent" 
          elevation={0}
          sx={{ 
            mt: 3,
          }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
              {title}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, overflowY: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  </Drawer>
);

const Settings = () => {
  const { logout, user, api } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    name: user?.name || '',
    email: user?.email || '',
    language: 'English',
    emailNotifications: true
  });
  const [openDrawer, setOpenDrawer] = useState('');
  const [editValue, setEditValue] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch user settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/users/settings');
      if (response.data.status === 'success') {
        setSettings(prev => ({
          ...prev,
          ...response.data.settings,
          email: response.data.settings.email || user?.email || ''  // Ensure email is preserved
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showSnackbar('Failed to load settings', 'error');
    }
  };

  const handleDrawerClose = () => {
    setOpenDrawer('');
    setEditValue('');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleUpdate = async (field, value) => {
    try {
      const response = await api.put('/api/users/settings', { [field]: value });
      if (response.data.status === 'success') {
        setSettings(prev => ({ ...prev, [field]: value }));
        showSnackbar('Settings updated successfully');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update settings', 'error');
    }
    handleDrawerClose();
  };

  const handleResetRecommendations = async () => {
    try {
      const response = await api.post('/api/users/reset-recommendations');
      if (response.data.status === 'success') {
        showSnackbar('Recommendations reset successfully');
      }
    } catch (error) {
      console.error('Error resetting recommendations:', error);
      showSnackbar('Failed to reset recommendations', 'error');
    }
    handleDrawerClose();
  };

  const handleLogout = async () => {
    try {
      handleDrawerClose();
      await logout();  // Use the logout function from AuthContext
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
      showSnackbar('Failed to logout', 'error');
    }
  };

  return (
    <Box sx={{ mb: 10, mt: 8, px: 2, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ mb: 4, mt: 10 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Profile and preferences
        </Typography>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2 }}>
          <SettingsItem 
            icon={<Person2Outlined />}
            primary="Name"
            secondary={settings.name}
            onClick={() => {
              setEditValue(settings.name);
              setOpenDrawer('name');
            }}
          />
          <SettingsItem 
            icon={<EmailOutlined />}
            primary="Email address"
            secondary={settings.email}
            onClick={() => {
              setEditValue(settings.email);
              setOpenDrawer('email');
            }}
          />
          <SettingsItem 
            icon={<LanguageOutlined />}
            primary="Language"
            secondary={settings.language}
            onClick={() => {
              setEditValue(settings.language);
              setOpenDrawer('language');
            }}
          />
          <SettingsItem 
            icon={<RestartAltOutlined />}
            primary="Reset Recommendation"
            secondary="Start Fresh"
            onClick={() => setOpenDrawer('reset')}
          />
        </List>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Feedback and support
        </Typography>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2 }}>
          <SettingsItem 
            icon={<StarBorderOutlined />}
            primary="Rate this app"
            secondary="Version 1.0.0"
            onClick={() => {/* Handle rate app */}}
          />
          <SettingsItem 
            icon={<FeedbackOutlined />}
            primary="Send Feedback"
            secondary="Help us improve"
            onClick={() => {/* Handle feedback */}}
          />
          <SettingsItem 
            icon={<PersonAddAltOutlined />}
            primary="Invite a friend"
            secondary="Share Lyke"
            onClick={() => {/* Handle invite */}}
          />
        </List>
      </Box>

      <Box sx={{ mt: 4 }}>
        <ButtonBase
          onClick={() => setOpenDrawer('logout')}
          component="div"
          sx={{
            width: '100%',
            textAlign: 'left',
            '&:hover': {
              backgroundColor: 'danger.light',
            },
            transition: 'all 0.3s ease',
            borderRadius: '12px',
            backgroundColor: 'danger.light',
          }}
        >
          <ListItem sx={{ py: 2, width: '100%' }}>
            <ListItemIcon>
              <Box
                sx={{
                  backgroundColor: 'danger.light',
                  borderRadius: '12px',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                }}
              >
                <LogoutOutlined sx={{ color: 'danger.main' }} />
              </Box>
            </ListItemIcon>
            <ListItemText 
              primary={<Typography sx={{ color: 'danger.main', fontWeight: 500 }}>Logout</Typography>}
            />
            <ChevronRight sx={{ color: 'danger.main', mr: 1 }} />
          </ListItem>
        </ButtonBase>
      </Box>

      {/* Replace Dialogs with Drawers */}
      <SettingsDrawer
        open={openDrawer === 'name'}
        onClose={handleDrawerClose}
        title="Wow you changed your name? Big move!"
      >
        
        <TextField
          autoFocus
          margin="dense"
          label="What’s your new name?"
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          sx={{ mb: 3 }}
        />
        <ButtonBase
          onClick={() => handleUpdate('name', editValue)}
          component="div"
          sx={{
            width: '100%',
            backgroundColor: 'primary.main',
            color: 'white',
            py: 2,
            borderRadius: 2,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Save
        </ButtonBase>
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'email'}
        onClose={handleDrawerClose}
        title="Update Email"
      >
        <TextField
          autoFocus
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          sx={{ mb: 3 }}
        />
        <ButtonBase
          onClick={() => handleUpdate('email', editValue)}
          component="div"
          sx={{
            width: '100%',
            backgroundColor: 'primary.main',
            color: 'white',
            py: 2,
            borderRadius: 2,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Save
        </ButtonBase>
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'language'}
        onClose={handleDrawerClose}
        title="Select Language"
      >
        <TextField
          select
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          sx={{ mb: 3 }}
        >
          <MenuItem value="English">English</MenuItem>
          <MenuItem value="Spanish">Spanish</MenuItem>
          <MenuItem value="French">French</MenuItem>
        </TextField>
        <ButtonBase
          onClick={() => handleUpdate('language', editValue)}
          component="div"
          sx={{
            width: '100%',
            backgroundColor: 'primary.main',
            color: 'white',
            py: 2,
            borderRadius: 2,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          Save
        </ButtonBase>
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'reset'}
        onClose={handleDrawerClose}
        title="Reset Recommendations"
      >
        <Typography sx={{ mb: 3 }}>
          This will reset all your recommendations and clear your like history. This action cannot be undone.
        </Typography>
        <ButtonBase
          onClick={handleResetRecommendations}
          component="div"
          sx={{
            width: '100%',
            backgroundColor: 'danger.main',
            color: 'white',
            py: 2,
            borderRadius: 2,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'danger.dark',
            }
          }}
        >
          Reset
        </ButtonBase>
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'logout'}
        onClose={handleDrawerClose}
        title="Logout"
      >
        <Typography sx={{ mb: 3 }}>
          Are you sure you want to logout?
        </Typography>
        <ButtonBase
          onClick={handleLogout}
          component="div"
          sx={{
            width: '100%',
            backgroundColor: 'danger.main',
            color: 'white',
            py: 2,
            borderRadius: 2,
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'danger.dark',
            }
          }}
        >
          Logout
        </ButtonBase>
      </SettingsDrawer>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
